import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  useHistory,
  useParams,
} from "react-router-dom/cjs/react-router-dom.min";
import httpclient from "../../service/httpclient";
import "./recruitment_question_exam.scss";
import {
  Button,
  Checkbox,
  Drawer,
  message,
  Modal,
  notification,
  Radio,
  Space,
  Spin,
  Tag,
} from "antd";
import RichText from "../../components/RichText/RichText";
import useTimer from "../../hooks/useTimer";
import shuffle from "../../utils/shuffle";
import { UserContext } from "../../App";
import { LeftOutlined } from "@ant-design/icons";
import useWindowWidth from "../../hooks/useWindowWidth";

const examCountdown = 60 * 60; //考试时间（秒）
const singleChoiceCount = 30; //单选题随机挑选数量
const multiChoiceCount = 10; //单选题随机挑选数量

function RecruitmentQuestionDescription({
  question,
  answers,
  handleSingleChoice,
  handleMultiChoice,
}) {
  return (
    <div>
      <div>
        <Space size="small">
          <Tag>{question.format}</Tag>
        </Space>
      </div>
      <div style={{ marginTop: 16 }}>
        <h3>题面</h3>
        <RichText
          style={{ margin: "0 16px" }}
          richText={question.content.description}
        />
      </div>
      {(question.format === "单选" || question.format === "多选") && (
        <div style={{ marginTop: 16 }}>
          <h3>选项</h3>
          {question.format === "多选" &&
            question.content.choices.map((choice, index) => (
              <div key={index}>
                <Space
                  style={{ margin: "0 16px", cursor: "pointer" }}
                  onClick={() =>
                    handleMultiChoice(
                      question.id,
                      index,
                      !answers[question.id]?.includes(index)
                    )
                  }
                >
                  <Checkbox checked={answers[question.id]?.includes(index)} />
                  <b>{String.fromCharCode(65 + index)}.</b>
                  <RichText richText={choice} />
                </Space>
              </div>
            ))}
          {question.format === "单选" && (
            <Radio.Group
              style={{
                display: "flex",
                flexDirection: "column",
                margin: "0 16px",
              }}
              value={answers[question.id]}
              options={question.content.choices.map((choice, index) => ({
                value: index,
                label: (
                  <div key={index}>
                    <Space>
                      <b>{String.fromCharCode(65 + index)}.</b>
                      <RichText richText={choice} />
                    </Space>
                  </div>
                ),
              }))}
              onChange={(e) => handleSingleChoice(question.id, e.target.value)}
            />
          )}
        </div>
      )}
    </div>
  );
}

function AnswerCard({
  questions,
  answers,
  goToQuestion,
  currentQuestion,
  start,
  end = undefined,
}) {
  return (
    <div style={{ display: "flex", flexWrap: "wrap", marginBottom: 16 }}>
      {questions.slice(start, end).map((q, i) => (
        <div
          key={q.id}
          style={{
            display: "inline-block",
            borderRadius: "50%",
            width: 46,
            height: 46,
            backgroundColor:
              answers[q.id] === undefined ? "#dfdfdf" : "#8dc7ff",
            alignContent: "center",
            textAlign: "center",
            fontSize: 16,
            margin: 4,
            transition: "all ease-in-out 0.2s",
            cursor: "pointer",
            border:
              start + i === currentQuestion
                ? "2px solid #8dc7ff"
                : "2px solid white",
            color: "#00000088",
          }}
          onClick={() => goToQuestion(start + i)}
        >
          <b>{start + i + 1}</b>
        </div>
      ))}
    </div>
  );
}

function MobileDrawerWrapper({ title, open, setOpen, children }) {
  const { deviceType } = useWindowWidth();
  return deviceType === "pc" ? (
    <>{children}</>
  ) : (
    <Drawer
      title={title}
      width="100vw"
      visible={open}
      onClose={() => setOpen(false)}
    >
      {children}
    </Drawer>
  );
}

function RecruitmentQuestionExamPage() {
  const { listId } = useParams();
  const [list, setList] = useState(null);
  useEffect(() => {
    httpclient
      .getOneRecruitmentQuestionListWithQuestionInfo(listId, 1)
      .then((l) => {
        setList(l);
      });
  }, [listId]);

  const [answerCardDrawerOpen, setAnswerCardDrawerOpen] = useState(false);

  const { userInfo } = useContext(UserContext);
  const LOCAL_STORAGE_PREFIX = useMemo(
    () => `exam-${userInfo?.id}-${listId}`,
    [listId, userInfo?.id]
  );
  const setLocalStartTime = useCallback(() => {
    localStorage.setItem(
      LOCAL_STORAGE_PREFIX + "-start-time",
      new Date().getTime()
    );
  }, [LOCAL_STORAGE_PREFIX]);
  const getLocalStartTime = useCallback(() => {
    const startTime = localStorage.getItem(
      LOCAL_STORAGE_PREFIX + "-start-time"
    );
    if (!startTime) return null;
    return parseInt(startTime);
  }, [LOCAL_STORAGE_PREFIX]);
  const setLocalSingleIds = useCallback(
    (ids) => {
      localStorage.setItem(
        LOCAL_STORAGE_PREFIX + "-single-ids",
        JSON.stringify(ids)
      );
    },
    [LOCAL_STORAGE_PREFIX]
  );
  const getLocalSingleIds = useCallback(() => {
    const ids = localStorage.getItem(LOCAL_STORAGE_PREFIX + "-single-ids");
    if (!ids) return null;
    return JSON.parse(ids);
  }, [LOCAL_STORAGE_PREFIX]);
  const setLocalMultiIds = useCallback(
    (ids) => {
      localStorage.setItem(
        LOCAL_STORAGE_PREFIX + "-multi-ids",
        JSON.stringify(ids)
      );
    },
    [LOCAL_STORAGE_PREFIX]
  );
  const getLocalMultiIds = useCallback(() => {
    const ids = localStorage.getItem(LOCAL_STORAGE_PREFIX + "-multi-ids");
    if (!ids) return null;
    return JSON.parse(ids);
  }, [LOCAL_STORAGE_PREFIX]);
  const setLocalAnswers = useCallback(
    (ans) => {
      localStorage.setItem(
        LOCAL_STORAGE_PREFIX + "-answers",
        JSON.stringify(ans)
      );
    },
    [LOCAL_STORAGE_PREFIX]
  );
  const getLocalAnswers = useCallback(() => {
    const ans = localStorage.getItem(LOCAL_STORAGE_PREFIX + "-answers");
    if (!ans) return null;
    return JSON.parse(ans);
  }, [LOCAL_STORAGE_PREFIX]);
  const clearLocal = () => {
    localStorage.removeItem(`exam-${userInfo.id}-${listId}-start-time`);
    localStorage.removeItem(`exam-${userInfo.id}-${listId}-single-ids`);
    localStorage.removeItem(`exam-${userInfo.id}-${listId}-multi-ids`);
    localStorage.removeItem(`exam-${userInfo.id}-${listId}-answers`);
  };

  const history = useHistory();
  if (list && !list.can_take_exam)
    history.push(`/recruitment_question_lists/${list.id}`);

  const { formatedCountdown, currentCountdown, start, time, setTime } =
    useTimer(0, false, examCountdown);
  useEffect(() => {
    if (list) {
      const startTime = getLocalStartTime();
      if (!startTime) {
        start();
        setLocalStartTime();
      } else {
        setTime(Math.floor((new Date().getTime() - startTime) / 1000));
        start();
      }
    }
  }, [getLocalStartTime, list, setLocalStartTime, setTime, start]);

  const questions = useMemo(() => {
    const mayNotUniqueQuestions =
      list?.content.flatMap((x) => x.questions) ?? [];
    const uniqueIds = [...new Set(mayNotUniqueQuestions.map((q) => q.id))];
    return uniqueIds.map((id) =>
      mayNotUniqueQuestions.find((q) => q.id === id)
    );
  }, [list]);

  const randomSingleChoiceQuestions = useMemo(() => {
    const ids = getLocalSingleIds();
    if (!ids) {
      const res = shuffle(questions.filter((q) => q.format === "单选")).slice(
        0,
        singleChoiceCount
      );
      if (res.length > 0) setLocalSingleIds(res.map((q) => q.id));
      return res;
    }
    return ids.flatMap((id) => questions.find((q) => q.id === id) ?? []);
  }, [getLocalSingleIds, questions, setLocalSingleIds]);
  const randomMultiChoiceQuestions = useMemo(() => {
    const ids = getLocalMultiIds();
    if (!ids) {
      const res = shuffle(questions.filter((q) => q.format === "多选")).slice(
        0,
        multiChoiceCount
      );
      if (res.length > 0) setLocalMultiIds(res.map((q) => q.id));
      return res;
    }
    return ids.flatMap((id) => questions.find((q) => q.id === id) ?? []);
  }, [getLocalMultiIds, questions, setLocalMultiIds]);
  const randomQuestions = useMemo(
    () => [...randomSingleChoiceQuestions, ...randomMultiChoiceQuestions],
    [randomMultiChoiceQuestions, randomSingleChoiceQuestions]
  );

  const [answers, setAnswers] = useState({});
  useEffect(() => {
    const ans = getLocalAnswers();
    if (ans) setAnswers(ans);
  }, [getLocalAnswers]);
  useEffect(() => {
    if (!!userInfo) setLocalAnswers(answers);
  }, [answers, setLocalAnswers, userInfo]);

  const handleSingleChoice = (questionId, choice) => {
    setAnswers((ans) => ({ ...ans, [questionId]: choice }));
  };
  const handleMultiChoice = (questionId, choice, checked) => {
    let currentAns = answers[questionId] ?? [];
    if (checked && !currentAns.includes(choice)) currentAns.push(choice);
    if (!checked && currentAns.includes(choice))
      currentAns = currentAns.filter((a) => a !== choice);

    currentAns = currentAns.sort((a, b) => a - b);
    if (currentAns.length === 0)
      setAnswers((ans) =>
        Object.fromEntries(
          Object.entries(ans).filter(([key]) => +key !== questionId)
        )
      );
    else setAnswers((ans) => ({ ...ans, [questionId]: currentAns }));
  };

  const [currentQuestion, setCurrentQuestion] = useState(0);
  const goToPreviousQuestion = () => {
    setCurrentQuestion((c) => (c === 0 ? 0 : c - 1));
  };
  const goToNextQuestion = () => {
    setCurrentQuestion((c) => (c + 1 === randomQuestions.length ? c : c + 1));
  };
  const goToQuestion = (index) => {
    setCurrentQuestion((c) =>
      0 <= index && index < randomQuestions.length ? index : c
    );
  };

  const submitted = useRef(false);
  const submitExam = () => {
    submitted.current = true;
    return httpclient.submitRecruitmentQuestionExamRecord(
      list.id,
      randomQuestions.map((q) => q.id),
      answers,
      time < examCountdown ? time : examCountdown
    );
  };

  if (currentCountdown === 0 && userInfo) {
    clearLocal();
    submitted.current === false &&
      submitExam().then(() => {
        notification.open({
          message: <b>已提交答卷</b>,
          description: "笔试时间已到，自动提交答卷",
          key: "auto_submit",
        });
        history.push(`/recruitment-question-exam-records/${list.id}`);
      });
  }

  return (
    <div className="recruitment-question-list-exam">
      {list ? (
        <>
          <div className="recruitment-question-description-wrapper">
            <div className="recruitment-question-answer-card-mobile">
              <b style={{ color: "#000000aa", fontSize: 16 }}>
                {formatedCountdown}
              </b>
              <Space
                size={2}
                onClick={() => setAnswerCardDrawerOpen(true)}
                style={{ cursor: "pointer" }}
              >
                <LeftOutlined />
                答题卡
              </Space>
            </div>
            <div className="recruitment-question-description">
              <RecruitmentQuestionDescription
                question={randomQuestions[currentQuestion]}
                answers={answers}
                handleSingleChoice={handleSingleChoice}
                handleMultiChoice={handleMultiChoice}
              />
              <div style={{ textAlign: "center" }}>
                <Button type="link" onClick={goToPreviousQuestion}>
                  {"<<"}上一题
                </Button>
                <Button type="link" onClick={goToNextQuestion}>
                  下一题{">>"}
                </Button>
              </div>
            </div>
          </div>
          <MobileDrawerWrapper
            title="答题卡"
            open={answerCardDrawerOpen}
            setOpen={setAnswerCardDrawerOpen}
          >
            <div className="recruitment-question-answer-card-pc">
              <h3>计时</h3>
              <h2 style={{ textAlign: "center", color: "#000000aa" }}>
                {formatedCountdown}
              </h2>
              <h3>单选题</h3>
              <AnswerCard
                questions={randomQuestions}
                answers={answers}
                goToQuestion={goToQuestion}
                currentQuestion={currentQuestion}
                start={0}
                end={randomSingleChoiceQuestions.length}
              />
              <h3>多选题</h3>
              <AnswerCard
                questions={randomQuestions}
                answers={answers}
                goToQuestion={goToQuestion}
                currentQuestion={currentQuestion}
                start={randomSingleChoiceQuestions.length}
              />

              <div style={{ textAlign: "center", marginTop: 16 }}>
                <Button
                  type="primary"
                  onClick={() => {
                    Modal.confirm({
                      title: "确认提交？",
                      content: (
                        <span>
                          共<b>{randomQuestions.length}</b>题，已作答
                          <b>{Object.keys(answers).length}</b>题，是否确认提交？
                        </span>
                      ),
                      okText: "确认",
                      cancelText: "取消",
                      onOk: () => {
                        submitExam().then(() => {
                          message.success("提交成功");
                          history.push(
                            `/recruitment-question-exam-records/${list.id}`
                          );
                        });
                      },
                    });
                  }}
                >
                  <b>提交答卷</b>
                </Button>
              </div>
            </div>
          </MobileDrawerWrapper>
        </>
      ) : (
        <div
          style={{
            minHeight: "70dvh",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            width: "100%",
          }}
        >
          <Spin />
        </div>
      )}
    </div>
  );
}

export default RecruitmentQuestionExamPage;
